import { CSSTransition } from "react-transition-group";
import { bisector } from "d3-array";
import { ScaleBand, scaleBand, ScaleLinear, scaleLinear } from "d3-scale";
import { arc, area, curveStepAfter, line, pie, PieArcDatum } from "d3-shape";
import { identity } from "lodash";
import { Fragment, ReactNode, useRef, useState } from "react";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { useI18n } from "@/stores/RootStoreContext";
import { classNames } from "@/utils/classNames";
import { immediate, when } from "@/utils/functions";
import { PreppedI18nState } from "@/i18n/i18nDomain";
import { extent } from "./sparklineHelpers";

const scaleVal = <T,>(s: PreppedSparklineScale<T>, d: T, i: number, ds: T[]): number => {
  switch (s.scaleType) {
    case "linear":
      return s.scale(s.val(d, i, ds));
    case "band":
      return s.scale(s.val(d, i, ds)) ?? 0;
  }
};

const scaleCenter = <T,>(s: PreppedSparklineScale<T>, d: T, i: number, ds: T[]): number => {
  switch (s.scaleType) {
    case "linear":
      return s.scale(s.val(d, i, ds));
    case "band":
      return (s.scale(s.val(d, i, ds)) ?? 0) + s.scale.bandwidth() / 2;
  }
};

export type SparklineLineProps<T> = {
  type: "line";
  area?: boolean;
  curve?: "linear" | "step";
  data?: T[];
  color?: string;
  strokeWidth?: number;
};

export const SparklineLine = <T,>(props: SparklineLineProps<T> & SparklineContext<T>) => {
  const x = props.x;
  const y = props.y;
  const data = props.data;
  const offset = props.x.scaleType === "band" ? props.x.scale.bandwidth() / 2 : 0;
  const l = line<T>()
    .x((d, i, ds) => scaleVal(x, d, i, ds) + offset)
    .y((d, i, ds) => scaleVal(y, d, i, ds));
  if (props.curve === "step") {
    l.curve(curveStepAfter);
  }
  const a =
    props.area &&
    area<T>()
      .x((d, i, ds) => scaleVal(x, d, i, ds))
      .y0(
        immediate(() => {
          switch (y.scaleType) {
            case "linear":
              return y.scale(y.domain[0]);
            case "band":
              return 0;
          }
        }),
      )
      .y1((d, i, ds) => scaleVal(y, d, i, ds));
  const color = props.color ?? "currentColor";
  return (
    <>
      <path fill={"none"} stroke={color} strokeWidth={props.strokeWidth ?? 2} d={String(l(data))} />
      {a && <path fill={color} fillOpacity={0.2} d={String(a(data))} />}
    </>
  );
};

export type SparklineBandProps<T> = {
  type: "band";
  data: T[];
  y0: (d: T) => number;
  y1: (d: T) => number;
  color: string;
  opacity?: number;
  curve?: "linear" | "step";
};

export const SparklineBand = <T,>(props: SparklineBandProps<T> & SparklineContext<T>) => {
  const { x, y } = props;
  if (y.scaleType !== "linear") {
    return null;
  }
  const a = area<T>()
    .x((d, i, ds) => scaleVal(x, d, i, ds))
    .y0((d) => y.scale(props.y0(d)))
    .y1((d) => y.scale(props.y1(d)));
  if (props.curve === "step") {
    a.curve(curveStepAfter);
  }
  return <path fill={props.color} fillOpacity={props.opacity ?? 1} d={String(a(props.data))} />;
};

export type SparklineDotsProps<T> = {
  type: "dots";
  data?: T[];
  color?: string;
  radius?: number;
  variant?: "filled" | "ring";
};

export const SparklineDots = <T,>(props: SparklineDotsProps<T> & SparklineContext<T>) => {
  const x = props.x;
  const y = props.y;
  const offset = props.x.scaleType === "band" ? props.x.scale.bandwidth() / 2 : 0;
  const color = props.color ?? "currentColor";
  const isRing = props.variant === "ring";
  return (
    <>
      {props.data.map((d, i, ds) => (
        <circle
          key={`dot-${i}`}
          cx={scaleVal(x, d, i, ds) + offset}
          cy={scaleVal(y, d, i, ds)}
          r={props.radius ?? 3}
          fill={isRing ? "none" : color}
          stroke={isRing ? color : undefined}
          strokeWidth={isRing ? 2 : undefined}
        />
      ))}
    </>
  );
};

export type SparklineBarsProps<T> = {
  type: "bars";
  data?: T[];
};

export const SparklineBars = <T,>({
  height,
  data,
  x,
  y,
}: SparklineBarsProps<T> & SparklineContext<T>) => {
  if (x.scaleType !== "band") {
    return null;
  }
  return (
    <>
      {data.map((d, i, ds) => {
        const yValue = y.val(d, i, ds);
        const hasValue = typeof yValue === "string" || !isNaN(yValue);
        return (
          hasValue && (
            <Fragment key={x.val(d, i, ds)}>
              <rect
                x={scaleVal(x, d, i, ds)}
                y={scaleVal(y, d, i, ds)}
                width={x.scale.bandwidth()}
                height={height - scaleVal(y, d, i, ds)}
                fill={"currentColor"}
                fillOpacity={0.2}
              />
              <rect
                x={scaleVal(x, d, i, ds)}
                y={scaleVal(y, d, i, ds)}
                width={x.scale.bandwidth()}
                height={2}
                fill={"currentColor"}
              />
            </Fragment>
          )
        );
      })}
    </>
  );
};

export type SparklineHorizontalBarsProps<T> = {
  type: "bars-horizontal";
  colors?: string[];
  data?: T[];
};

export const SparklineHorizontalBars = <T,>({
  data,
  x,
  y,
  colors,
}: SparklineHorizontalBarsProps<T> & SparklineContext<T>) => {
  if (x.scaleType !== "linear" || y.scaleType !== "band") {
    return null;
  }

  const zeroPoint = Math.min(Math.max(0, x.domain[0]), x.domain[1]);
  const baseline = x.scale(zeroPoint);

  return (
    <>
      {data.map((d, i, ds) => {
        const valueX = scaleVal(x, d, i, ds);
        const barStart = Math.min(baseline, valueX);
        const barWidth = Math.abs(valueX - baseline);
        const markerWidth = Math.min(2, barWidth);
        const markerX = valueX >= baseline ? valueX - markerWidth : valueX;
        const color = colors && colors.length > 0 ? colors[i % colors.length] : "currentColor";

        return (
          <Fragment key={y.val(d, i, ds)}>
            <rect
              x={barStart}
              y={scaleVal(y, d, i, ds)}
              width={barWidth}
              height={y.scale.bandwidth()}
              fill={color}
              fillOpacity={0.77}
            />
            <rect
              x={markerX}
              y={scaleVal(y, d, i, ds)}
              width={markerWidth}
              height={y.scale.bandwidth()}
              fill={color}
            />
          </Fragment>
        );
      })}
    </>
  );
};

export type SparklineXAxisProps = {
  type: "x-axis";
};

export const SparklineXAxis = <T,>({
  data,
  x,
  height,
}: SparklineXAxisProps & SparklineContext<T>) => {
  const bandwidth = x.scaleType === "band" ? x.scale.bandwidth() : 0;
  const padding = x.scaleType === "band" ? x.scale.padding() : 0;
  const width = Math.floor(bandwidth + bandwidth * padding * 2);
  return (
    <div
      className={classNames(
        "absolute",
        "top-0",
        "left-0",
        "color-default",
        "text-center",
        bandwidth && bandwidth < 50 ? "text-xs" : "text-sm",
      )}
    >
      {data.map((d, i, ds) => (
        <div
          key={i}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            transform: `translate(${scaleCenter(x, d, i, ds) - width / 2}px, calc(${height}px + 1em))`,
            width: width,
          }}
          data-tooltip={x.formatTickVal ? x.formatVal(d, i, ds) : undefined}
        >
          {x.formatTickVal ? x.formatTickVal(d, i, ds) : x.formatVal(d, i, ds)}
        </div>
      ))}
    </div>
  );
};

export type SparklineHoverValueProps = { type: "hover-value" };

export const SparklineHoverValue = <T,>({
  margin,
  hoverData,
}: SparklineHoverValueProps & SparklineContext<T>) => {
  const ref = useRef(null);
  return (
    <CSSTransition
      in={hoverData !== undefined}
      classNames={{
        appear: "transition-opacity opacity-0",
        appearDone: "transition-opacity opacity-100",
        exit: "transition-opacity opacity-100",
        exitDone: "transition-opacity opacity-0",
      }}
      nodeRef={ref}
      timeout={300}
    >
      <div
        ref={ref}
        className={classNames(
          "absolute",
          "color-default",
          "pointer-events-none",
          "whitespace-pre",
          "left-0",
          "top-0",
          "transition-all",
        )}
        style={{
          transform: [
            `translate(${margin.left + (hoverData?.x ?? 0)}px,${margin.top + (hoverData?.y ?? 0)}px)`,
            "translate(-50%, -100%)",
          ].join(" "),
        }}
      >
        {hoverData?.formattedVal}
      </div>
    </CSSTransition>
  );
};

export type SparklineValueProps = { type: "value" };

export const SparklineValue = <T,>({
  margin,
  data,
  x,
  y,
}: SparklineValueProps & SparklineContext<T>) => {
  return (
    <>
      {data.map((d, i, ds) => (
        <div
          key={i}
          className={"absolute pointer-events-none left-0 top-0 color-default text-xs"}
          style={{
            transform: [
              `translate(${margin.left + (scaleCenter(x, d, i, ds) ?? 0)}px,${margin.top + (scaleVal(y, d, i, ds) ?? 0)}px)`,
              "translate(-50%, -100%)",
            ].join(" "),
          }}
        >
          {y.formatVal(d, i, ds)}
        </div>
      ))}
    </>
  );
};

export type SparklineTick = { value: number; label?: string };

export type SparklinePlotBackgroundProps = { type: "plot-background"; opacity?: number };

export const SparklinePlotBackground = <T,>({
  width,
  height,
  opacity = 0.05,
}: SparklinePlotBackgroundProps & SparklineContext<T>) => (
  <rect
    className={"subtle"}
    x={0}
    y={0}
    width={width}
    height={height}
    fill={"currentColor"}
    fillOpacity={opacity}
  />
);

export type SparklineYAxisProps = {
  type: "y-axis";
  ticks: SparklineTick[];
  gridlines?: boolean;
  label?: string;
};

export const SparklineYAxis = <T,>({
  ticks,
  gridlines = true,
  label,
  width,
  height,
  margin,
  y,
}: SparklineYAxisProps & SparklineContext<T>) => {
  if (y.scaleType !== "linear") {
    return null;
  }
  return (
    <g className={"subtle"} fontSize={11}>
      {label !== undefined && (
        <text
          transform={"rotate(-90)"}
          x={-height / 2}
          y={-margin.left + 12}
          textAnchor={"middle"}
          fill={"currentColor"}
        >
          {label}
        </text>
      )}
      {ticks.map((tick) => (
        <g key={tick.value}>
          {gridlines && (
            <line
              x1={0}
              x2={width}
              y1={y.scale(tick.value)}
              y2={y.scale(tick.value)}
              stroke={"currentColor"}
              strokeOpacity={0.15}
            />
          )}
          {tick.label !== undefined && (
            <text
              x={-8}
              y={y.scale(tick.value)}
              textAnchor={"end"}
              dominantBaseline={"middle"}
              fill={"currentColor"}
            >
              {tick.label}
            </text>
          )}
        </g>
      ))}
    </g>
  );
};

export type SparklineXTicksProps = { type: "x-ticks"; ticks: SparklineTick[] };

export const SparklineXTicks = <T,>({
  ticks,
  height,
  x,
}: SparklineXTicksProps & SparklineContext<T>) => {
  if (x.scaleType !== "linear") {
    return null;
  }
  return (
    <g className={"subtle"} fontSize={11}>
      {ticks.map((tick) => (
        <text
          key={tick.value}
          x={x.scale(tick.value)}
          y={height + 20}
          textAnchor={"middle"}
          fill={"currentColor"}
        >
          {tick.label}
        </text>
      ))}
    </g>
  );
};

export type SparklineRuleProps = {
  type: "x-rule" | "y-rule";
  value: number;
  color?: string;
  opacity?: number;
  dashed?: boolean;
  label?: string;
  labelAnchor?: "start" | "middle" | "end";
  labelPlacement?: "inside-top" | "above";
  labelBold?: boolean;
  labelDy?: number;
  extendAbove?: number;
};

export const SparklineRule = <T,>(props: SparklineRuleProps & SparklineContext<T>) => {
  const { width, height, x, y } = props;
  const color = props.color ?? "currentColor";
  const opacity = props.opacity ?? 0.5;
  const vertical = props.type === "x-rule";
  if (vertical ? x.scaleType !== "linear" : y.scaleType !== "linear") {
    return null;
  }
  const pos = vertical
    ? x.scaleType === "linear"
      ? x.scale(props.value)
      : 0
    : y.scaleType === "linear"
      ? y.scale(props.value)
      : 0;
  const top = vertical ? -(props.extendAbove ?? 0) : 0;
  const labelY = props.labelPlacement === "above" ? top - 6 : 12 + (props.labelDy ?? 0);
  return (
    <g className={props.color ? undefined : "subtle"} fontSize={11}>
      <line
        x1={vertical ? pos : 0}
        x2={vertical ? pos : width}
        y1={vertical ? top : pos}
        y2={vertical ? height : pos}
        stroke={color}
        strokeOpacity={opacity}
        strokeDasharray={props.dashed ? "4 4" : undefined}
      />
      {props.label !== undefined && (
        <text
          x={vertical ? pos + (props.labelAnchor === "middle" ? 0 : 6) : -8}
          y={vertical ? labelY : pos}
          textAnchor={props.labelAnchor ?? (vertical ? "start" : "end")}
          dominantBaseline={vertical ? undefined : "middle"}
          fill={color}
          fontWeight={props.labelBold ? 600 : undefined}
        >
          {props.label}
        </text>
      )}
    </g>
  );
};

export type SparklineXRange = { from: number; to: number; color: string; title?: string };

export type SparklineXRangesProps = {
  type: "x-ranges";
  ranges: SparklineXRange[];
  offsetY: number;
  thickness?: number;
  label?: string;
};

export const SparklineXRanges = <T,>(props: SparklineXRangesProps & SparklineContext<T>) => {
  const { x, margin } = props;
  if (x.scaleType !== "linear") {
    return null;
  }
  const [domainFrom, domainTo] = x.domain;
  const visible = props.ranges.filter((range) => range.to >= domainFrom && range.from <= domainTo);
  return (
    <g fontSize={13}>
      {props.label !== undefined && (
        <text x={-margin.left} y={props.offsetY} dominantBaseline={"middle"} fill={"currentColor"}>
          {props.label}
        </text>
      )}
      {visible.map((range) => (
        <line
          key={`${range.from}-${range.to}`}
          x1={x.scale(Math.max(range.from, domainFrom))}
          x2={x.scale(Math.min(range.to, domainTo))}
          y1={props.offsetY}
          y2={props.offsetY}
          stroke={range.color}
          strokeWidth={props.thickness ?? 6}
          strokeLinecap={"round"}
        >
          {range.title !== undefined && <title>{range.title}</title>}
        </line>
      ))}
    </g>
  );
};

export type SparklineAnnotation = {
  key: string;
  x: number;
  y: number;
  content: ReactNode;
  placement?: "above" | "right";
  variant?: "card" | "dark";
  dy?: number;
};

export type SparklineAnnotationsProps = { type: "annotations"; annotations: SparklineAnnotation[] };

export const SparklineAnnotations = <T,>({
  annotations,
  margin,
  x,
  y,
}: SparklineAnnotationsProps & SparklineContext<T>) => {
  if (x.scaleType !== "linear" || y.scaleType !== "linear") {
    return null;
  }
  return (
    <>
      {annotations.map((annotation) => (
        <div
          key={annotation.key}
          className={classNames(
            "absolute",
            "left-0",
            "top-0",
            "pointer-events-none",
            "whitespace-nowrap",
            "rounded-sm",
            "px-2",
            "py-1",
            "text-xs",
            annotation.variant === "dark"
              ? ["bg-gray-x-hard", "font-semibold", "uppercase"]
              : ["bg-default", "color-default", "border", "border-gray-soft", "shadow-sm"],
          )}
          style={{
            color: annotation.variant === "dark" ? "var(--color-bg-default)" : undefined,
            transform: [
              `translate(${margin.left + x.scale(annotation.x)}px,${margin.top + y.scale(annotation.y) + (annotation.dy ?? 0)}px)`,
              annotation.placement === "right"
                ? "translate(10px, -50%)"
                : "translate(-50%, calc(-100% - 8px))",
            ].join(" "),
          }}
        >
          {annotation.content}
        </div>
      ))}
    </>
  );
};

export type SparklineComponent<T> =
  | SparklineBandProps<T>
  | SparklineAnnotationsProps
  | SparklineLineProps<T>
  | SparklineBarsProps<T>
  | SparklineHorizontalBarsProps<T>
  | SparklineDotsProps<T>
  | SparklineXAxisProps
  | SparklineHoverValueProps
  | SparklineValueProps
  | SparklinePlotBackgroundProps
  | SparklineYAxisProps
  | SparklineXTicksProps
  | SparklineRuleProps
  | SparklineXRangesProps;

export type SparklineScale<T> =
  | {
      scaleType: "linear";
      val: (d: T, i: number, ds: T[]) => number | undefined;
      formatVal?: (d: T, i: number, ds: T[]) => string | TMessage | TMessageParam;
      formatTickVal?: (d: T, i: number, ds: T[]) => string | TMessage | TMessageParam;
      domain?: [number, number];
    }
  | {
      scaleType: "band";
      val: (d: T, i: number, ds: T[]) => string;
      formatVal?: (d: T, i: number, ds: T[]) => string | TMessage | TMessageParam;
      formatTickVal?: (d: T, i: number, ds: T[]) => string | TMessage | TMessageParam;
    };

export type PreppedSparklineScale<T> =
  | {
      scaleType: "linear";
      val: (d: T, i: number, ds: T[]) => number;
      formatVal: (d: T, i: number, ds: T[]) => string;
      formatTickVal?: (d: T, i: number, ds: T[]) => string;
      domain: [number, number];
      scale: ScaleLinear<number, number>;
      invert: (v: number) => number;
    }
  | {
      scaleType: "band";
      val: (d: T, i: number, ds: T[]) => string;
      formatVal: (d: T, i: number, ds: T[]) => string;
      formatTickVal?: (d: T, i: number, ds: T[]) => string;
      domain: string[];
      scale: ScaleBand<string>;
      invert: (v: number) => string;
    };

export type SparklineProps<T> = {
  data?: T[];
  x: SparklineScale<T>;
  y: SparklineScale<T>;
  components: SparklineComponent<T>[];
  margin?: { top: number; left: number; bottom: number; right: number };
};

export type SparklineContext<T> = {
  margin: { top: number; left: number; bottom: number; right: number };
  width: number;
  height: number;
  data: T[];
  x: PreppedSparklineScale<T>;
  y: PreppedSparklineScale<T>;
  axisHeight: number;
  hoverData?: {
    formattedVal: string;
    x: number;
    y: number;
  };
};

const makeFormat =
  <T,>(
    f: (d: T, i: number, ds: T[]) => number | string | TMessage | TMessageParam | undefined,
    i18n: PreppedI18nState,
  ): PreppedSparklineScale<T>["formatVal"] =>
  (...args) => {
    const val = f(...args);
    switch (typeof val) {
      case "number":
        return String(val);
      case "string":
        return val;
      default:
        return i18n.tf(val);
    }
  };

const prepScale = <T,>(
  data: T[],
  size: number,
  orientation: "horizontal" | "vertical",
  s: SparklineScale<T>,
  i18n: PreppedI18nState,
): PreppedSparklineScale<T> => {
  const range = orientation === "horizontal" ? [0, size] : [size, 0];
  switch (s.scaleType) {
    case "linear": {
      const domain = s.domain ?? extent(data, s.val);
      const scale = scaleLinear(domain, range);
      const val = (d: T, i: number, ds: T[]): number => s.val(d, i, ds) ?? NaN;
      return {
        ...s,
        val,
        formatVal: makeFormat(s.formatVal ?? val, i18n),
        formatTickVal: s.formatTickVal && makeFormat(s.formatTickVal, i18n),
        domain,
        scale,
        invert: (v) => scale.invert(v),
      };
    }
    case "band": {
      const domain = data.map(s.val);
      const scale = scaleBand().domain(domain).range(range).padding(0.5);
      return {
        ...s,
        formatVal: makeFormat(s.formatVal ?? s.val, i18n),
        formatTickVal: s.formatTickVal && makeFormat(s.formatTickVal, i18n),
        domain,
        scale,
        invert: (v) => {
          const paddingOuter = scale(domain[0]) ?? 0;
          const index = Math.floor((v - paddingOuter) / scale.step());
          return domain[index];
        },
      };
    }
  }
};

const prepXScale = <T,>(
  data: T[],
  width: number,
  x: SparklineScale<T>,
  i18n: PreppedI18nState,
): PreppedSparklineScale<T> => prepScale(data, width, "horizontal", x, i18n);

const prepYScale = <T,>(
  data: T[],
  height: number,
  y: SparklineScale<T>,
  i18n: PreppedI18nState,
): PreppedSparklineScale<T> => prepScale(data, height, "vertical", y, i18n);

export const Sparkline = <T,>(props: SparklineProps<T> & { width: number; height: number }) => {
  const i18n = useI18n();
  const [hoveredDataIndex, setHoveredDataIndex] = useState<number>(-1);
  const xAxisComponent = props.components.find((c) => c.type === "x-axis");
  const valueComponent = props.components.find((c) => c.type === "value");
  const hoverValueComponent = props.components.find((c) => c.type === "hover-value");
  const annotationsComponents = props.components.filter((c) => c.type === "annotations");
  const data = props.data ?? [];
  const axisHeight = xAxisComponent ? 16 : 0;
  const margin = props.margin ?? { top: 8, left: 3, bottom: 8, right: 3 };
  const innerWidth = props.width - margin.left - margin.right;
  const innerHeight = props.height - margin.top - margin.bottom - axisHeight;
  const x = prepXScale(data, innerWidth, props.x, i18n);
  const y = prepYScale(data, innerHeight, props.y, i18n);
  const context: SparklineContext<T> = {
    axisHeight,
    margin,
    data,
    width: innerWidth,
    height: innerHeight,
    x: x,
    y: y,
    hoverData: when(hoveredDataIndex >= 0 && data.length > 0, () => {
      const d = data[hoveredDataIndex];
      return {
        formattedVal: y.formatVal(d, hoveredDataIndex, data),
        x: scaleCenter(x, d, hoveredDataIndex, data),
        y: scaleVal(y, d, hoveredDataIndex, data),
      };
    }),
  };
  return (
    <div className={classNames("relative", "overflow-visible")}>
      <svg
        width={props.width}
        height={props.height}
        pointerEvents={"all"}
        onMouseMove={(e) => {
          if (context.data.length === 0) {
            return;
          }
          const svg = e.currentTarget as SVGGraphicsElement;
          const pt = new DOMPoint(e.clientX, e.clientY);
          const loc = pt.matrixTransform(svg.getScreenCTM()!.inverse());
          const plotX = loc.x - margin.left;
          const plotY = loc.y - margin.top;
          if (context.x.scaleType === "linear" && context.y.scaleType === "band") {
            const v = context.y.invert(plotY);
            setHoveredDataIndex(
              context.data.findIndex((d, i, ds) => context.y.val(d, i, ds) === v),
            );
          } else if (context.x.scaleType === "linear") {
            const xVals = context.data
              .map(context.x.val)
              .filter((x): x is number => x !== undefined);
            const b = bisector<number, number>(identity);
            const index = b.center(xVals, context.x.invert(plotX));
            setHoveredDataIndex(index);
          } else {
            const v = context.x.invert(plotX);
            setHoveredDataIndex(
              context.data.findIndex((d, i, ds) => context.x.val(d, i, ds) === v),
            );
          }
        }}
        onMouseLeave={() => setHoveredDataIndex(-1)}
      >
        <g transform={"translate(" + margin.left + "," + margin.top + ")"}>
          {props.components.map((c, i) => {
            switch (c.type) {
              case "plot-background":
                return <SparklinePlotBackground key={i} {...context} {...c} />;
              case "y-axis":
                return <SparklineYAxis key={i} {...context} {...c} />;
              case "x-ticks":
                return <SparklineXTicks key={i} {...context} {...c} />;
              case "band":
                return <SparklineBand key={i} {...context} {...c} />;
              case "line":
                return <SparklineLine key={i} {...context} {...c} />;
              case "bars":
                return <SparklineBars key={i} {...context} {...c} />;
              case "bars-horizontal":
                return <SparklineHorizontalBars key={i} {...context} {...c} />;
              case "dots":
                return <SparklineDots key={i} {...context} {...c} />;
              case "x-rule":
              case "y-rule":
                return <SparklineRule key={i} {...context} {...c} />;
              case "x-ranges":
                return <SparklineXRanges key={i} {...context} {...c} />;
            }
          })}
        </g>
      </svg>
      {xAxisComponent && <SparklineXAxis {...xAxisComponent} {...context} />}
      {valueComponent && <SparklineValue {...valueComponent} {...context} />}
      {annotationsComponents.map((c, i) => (
        <SparklineAnnotations key={`annotations-${i}`} {...context} {...c} />
      ))}
      {hoverValueComponent && <SparklineHoverValue {...hoverValueComponent} {...context} />}
    </div>
  );
};

export type PieChartDatum = { value: number; label: string | TMessage; isTotal?: boolean };

export type PieChartProps = {
  data: PieChartDatum[];
};

export const PieChart = ({
  width,
  height,
  data,
}: PieChartProps & { width: number; height: number }) => {
  const p = pie<PieChartDatum>().value((d) => d.value);
  const a = arc<PieArcDatum<PieChartDatum>>()
    .innerRadius(0)
    .outerRadius(Math.min(width, height) / 2);
  const arcs = p(data);
  const opacity = scaleLinear()
    .domain(
      extent(
        data.filter((d) => !d.isTotal),
        (d) => d.value,
      ),
    )
    .range([20, 100]);

  return (
    <svg
      width={width}
      height={height}
      viewBox={[-width / 2, -height / 2, width, height].join()}
      pointerEvents={"all"}
    >
      <g stroke={"var(--color-bg-default)"}>
        {arcs.map((d) => (
          <path
            key={d.index}
            fill={"currentColor"}
            d={a(d) ?? ""}
            opacity={d.data.isTotal ? 0.2 : opacity(d.value) / 100}
          />
        ))}
      </g>
    </svg>
  );
};
